/**
 * Cart behaviour for snippets/cart-drawer.liquid (overlay drawer) and
 * sections/dorahi-cart.liquid (/cart page).
 * Vanilla port of dorahi-main/components/cart/modal.tsx plus
 * edit-item-quantity-button.tsx and delete-item-button.tsx.
 *
 * Everything is wired for real against the Ajax Cart API and refreshed from
 * the Section Rendering API, so the UI always reflects the server cart:
 *   - open / close the overlay drawer (backdrop, slide-in, Esc, scroll lock)
 *   - quantity + / - and remove          -> /cart/change.js
 *   - coupon apply / remove              -> /cart/update.js { discount }
 *   - price breakdown expand/collapse
 *   - coupon success popup + confetti
 *   - checkout via Shiprocket buyDirect, falling back to /checkout
 *
 * Handlers are delegated from document so they survive every refresh, and
 * Horizon's cart bubble keeps updating because we still dispatch
 * CartLinesUpdateEvent / CartUpdateEvent on mutations.
 */
import { CartLinesUpdateEvent } from "@shopify/events";

if (!window.__dorahiCartBound) {
  window.__dorahiCartBound = true;

  const DRAWER_SECTION_ID = "cart-drawer-section";

  const drawer = () => document.querySelector("dorahi-cart-drawer");
  const cartPage = () => document.querySelector("[data-dorahi-cart-page]");
  const scope = () => drawer() || cartPage() || document;
  const q = (selector) => scope().querySelector(selector);

  const sectionId = () =>
    drawer() ? DRAWER_SECTION_ID : cartPage()?.dataset.sectionId;

  /* ---------------- open / close (modal.tsx Dialog + Transition) ---------------- */

  let closeTimer = null;

  const openDrawer = () => {
    const root = drawer();
    if (!root) {
      // Drawer isn't on this template (e.g. /cart) — nothing to open.
      return;
    }
    if (closeTimer) clearTimeout(closeTimer);
    root.classList.remove("hidden");
    document.body.style.overflow = "hidden";
    requestAnimationFrame(() => {
      root
        .querySelector("[data-cart-backdrop]")
        ?.classList.replace("opacity-0", "opacity-100");
      root
        .querySelector("[data-cart-panel]")
        ?.classList.remove("translate-x-full");
    });
  };

  const closeDrawer = () => {
    const root = drawer();
    if (!root) return;
    root
      .querySelector("[data-cart-backdrop]")
      ?.classList.replace("opacity-100", "opacity-0");
    root.querySelector("[data-cart-panel]")?.classList.add("translate-x-full");
    document.body.style.overflow = "";
    closeTimer = setTimeout(() => root.classList.add("hidden"), 300);
  };

  const isOpen = () => {
    const root = drawer();
    return Boolean(root) && !root.classList.contains("hidden");
  };

  /* ---------------- refresh from the Section Rendering API ---------------- */

  // Re-render the cart markup server-side and swap it in, so item rows,
  // tier progress, coupon lists and totals all come from the real cart.
  const refresh = async ({ keepSheetOpen = false } = {}) => {
    const id = sectionId();
    if (!id) return;

    try {
      const response = await fetch(
        `${window.location.pathname}?section_id=${id}`,
        {
          headers: { "X-Requested-With": "XMLHttpRequest" },
        },
      );
      const html = await response.text();
      swapFromSectionHtml(html, { keepSheetOpen });
    } catch (error) {
      console.warn("[dorahi-cart] refresh failed:", error);
    }
  };

  const swapFromSectionHtml = (html, { keepSheetOpen = false } = {}) => {
    const parsed = new DOMParser().parseFromString(html, "text/html");

    const root = drawer();
    if (root) {
      const fresh = parsed.querySelector("[data-cart-drawer-body]");
      const current = root.querySelector("[data-cart-drawer-body]");
      if (fresh && current) {
        current.replaceWith(fresh);
        const count =
          parsed.querySelector("dorahi-cart-drawer")?.dataset.itemCount;
        if (count != null) root.dataset.itemCount = count;
      }
    } else {
      const page = cartPage();
      const fresh = parsed.querySelector("[data-dorahi-cart-page]");
      if (page && fresh) page.replaceWith(fresh);
    }

    if (keepSheetOpen) openSheet({ instant: true });
    window.dispatchEvent(new CustomEvent("dorahi:squircle:refresh"));
  };

  /* ---------------- coupons bottom sheet (slide up/down) ---------------- */

  let sheetTimer = null;

  const sheetParts = () => {
    const sheet = q("[data-coupons-sheet]");
    if (!sheet) return null;
    return {
      sheet,
      backdrop: sheet.querySelector("[data-coupons-backdrop]"),
      panel: sheet.querySelector("[data-coupons-panel]"),
    };
  };

  const isSheetOpen = () => {
    const sheet = q("[data-coupons-sheet]");
    return Boolean(sheet) && !sheet.classList.contains("hidden");
  };

  const openSheet = ({ instant = false } = {}) => {
    const parts = sheetParts();
    if (!parts) return;
    if (sheetTimer) clearTimeout(sheetTimer);

    parts.sheet.classList.remove("hidden");
    const show = () => {
      parts.backdrop?.classList.replace("opacity-0", "opacity-100");
      parts.panel?.classList.remove("translate-y-full");
    };

    if (instant) {
      // Re-showing after a section swap: the sheet was already open, so skip
      // the slide-up instead of replaying it on every coupon change.
      if (parts.backdrop) parts.backdrop.style.transitionDuration = "0s";
      if (parts.panel) parts.panel.style.transitionDuration = "0s";
      show();
      requestAnimationFrame(() => {
        if (parts.backdrop) parts.backdrop.style.transitionDuration = "";
        if (parts.panel) parts.panel.style.transitionDuration = "";
      });
    } else {
      requestAnimationFrame(show);
    }
  };

  const closeSheet = () => {
    const parts = sheetParts();
    if (!parts || parts.sheet.classList.contains("hidden")) return;
    parts.backdrop?.classList.replace("opacity-100", "opacity-0");
    parts.panel?.classList.add("translate-y-full");
    if (sheetTimer) clearTimeout(sheetTimer);
    sheetTimer = setTimeout(() => parts.sheet.classList.add("hidden"), 300);
  };

  /* ---------------- cart mutations ---------------- */

  const showLineError = (button, message) => {
    const item = button.closest("[data-cart-line-item]");
    const errorRow = item?.querySelector("[data-line-error]");
    if (!errorRow) return;
    errorRow.querySelector("[data-line-error-text]").textContent = message;
    errorRow.classList.remove("hidden");
    errorRow.classList.add("flex");
    setTimeout(() => {
      errorRow.classList.add("hidden");
      errorRow.classList.remove("flex");
    }, 5000);
  };

  let mutating = false;

  const changeLine = async (button, line, quantity) => {
    if (mutating) return;
    mutating = true;

    const routes = window.Theme?.routes ?? {};
    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(
      new CartLinesUpdateEvent({
        action: quantity === 0 ? "remove" : "update",
        context: "cart",
        lines: [{ quantity }],
        promise: deferred.promise,
      }),
    );

    try {
      const response = await fetch(
        routes.cart_change_url || "/cart/change.js",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({ line, quantity }),
        },
      );
      const result = await response.json();

      if (!response.ok || result.status) {
        // Stock limits and similar come back as a 422 with a message but no
        // cart payload, so resolve the event from the real cart instead of
        // trying to build one out of the error body.
        const current = await fetch(`${routes.cart_url || "/cart"}.js`, {
          headers: { Accept: "application/json" },
        })
          .then((r) => r.json())
          .catch(() => null);
        if (current) {
          deferred.resolve({
            cart: CartLinesUpdateEvent.createCartFromAjaxResponse(current),
            detail: {
              items: current.items,
              source: "dorahi-cart",
              itemCount: current.item_count,
              didError: true,
            },
          });
        } else {
          deferred.reject(new Error(result.message || "Cart update failed"));
        }
        // The cart is unchanged on a rejected line update, so keep the current
        // markup (and the inline message) instead of re-rendering it away.
        button.querySelector("[data-remove-spinner]")?.classList.add("hidden");
        button.querySelector("[data-remove-icon]")?.classList.remove("hidden");
        showLineError(
          button,
          result.description || result.message || "Could not update item",
        );
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(result),
        detail: {
          items: result.items,
          source: "dorahi-cart",
          itemCount: result.item_count,
          didError: false,
        },
      });

      await refresh();
    } catch (error) {
      deferred.reject(error);
      console.warn("[dorahi-cart] cart change failed:", error);
      showLineError(button, "Could not update item");
    } finally {
      mutating = false;
    }

    // Contents changed, so the qualifying offer may have too — e.g. a second
    // BOGO item just made HOS_BOGO valid. Runs after `mutating` clears so the
    // discount update isn't blocked by this one.
    await syncAutoCoupon();
  };

  /* ---------------- coupons ---------------- */

  // A coupon tap that lands mid-refresh used to be dropped on the floor,
  // leaving the button stuck on "Applying...". Wait the mutation out instead.
  const waitForIdle = async () => {
    for (let i = 0; i < 40 && mutating; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
    return !mutating;
  };

  // modal.tsx triggerPopupConfetti
  const popupConfetti = () => {
    if (typeof window.confetti !== "function") return;
    window.confetti({
      particleCount: 60,
      spread: 70,
      origin: { x: 0.5, y: 0.4 },
      colors: ["#ec4899", "#2c874a", "#fbbf24", "#8b5cf6", "#f472b6"],
      startVelocity: 20,
      gravity: 0.8,
      scalar: 0.7,
      ticks: 150,
      zIndex: 10000,
    });
  };

  let popupTimeout = null;

  const showCouponPopup = (code) => {
    const popup = q("[data-coupon-popup]");
    if (!popup) return;

    popup.querySelector("[data-coupon-popup-code]").textContent = code;

    // Savings for this code, read from the freshly rendered applied list.
    const savingsLine = scope()
      .querySelector(`[data-coupon-remove="${code}"]`)
      ?.closest("div")
      ?.querySelector("p")?.textContent;
    const savings = savingsLine
      ? parseFloat(savingsLine.replace(/[^\d.]/g, "")) || 0
      : 0;

    const savingsEl = popup.querySelector("[data-coupon-popup-savings]");
    const genericEl = popup.querySelector("[data-coupon-popup-generic]");
    if (savings > 0) {
      savingsEl.innerHTML = `You saved <span class="font-medium">₹</span>${savings.toLocaleString("en-IN", { maximumFractionDigits: 2 })} with this coupon.`;
      savingsEl.classList.remove("hidden");
      genericEl.classList.add("hidden");
    } else {
      savingsEl.classList.add("hidden");
      genericEl.classList.remove("hidden");
    }

    popup.classList.remove("hidden");
    popup.classList.add("flex");
    popupConfetti();
    if (popupTimeout) clearTimeout(popupTimeout);
    popupTimeout = setTimeout(hideCouponPopup, 4000);
  };

  const hideCouponPopup = () => {
    if (popupTimeout) clearTimeout(popupTimeout);
    const popup = q("[data-coupon-popup]");
    popup?.classList.add("hidden");
    popup?.classList.remove("flex");
  };

  const showCouponMessage = (type, text) => {
    const el = q("[data-coupon-message]");
    if (!el) return;
    el.textContent = text;
    el.classList.remove(
      "hidden",
      "bg-green-50",
      "text-green-700",
      "bg-red-50",
      "text-red-700",
    );
    el.classList.add(
      "flex",
      type === "success" ? "bg-green-50" : "bg-red-50",
      type === "success" ? "text-green-700" : "text-red-700",
    );
    setTimeout(() => {
      el.classList.add("hidden");
      el.classList.remove("flex");
    }, 3000);
  };

  /* ---------------- auto-applied coupon ---------------- */

  // Discount codes never combine on this store (every one is combinesWith:false
  // in Shopify), so the cart carries exactly one. Liquid decides which it
  // *should* be -- HOS_BOGO once the bag holds enough BOGO-collection items,
  // otherwise the fallback -- and publishes that on the swap root. We only push
  // the change; Shopify still works out what the discount is worth.
  const MANUAL_KEY = "dorahi:coupon:manual";
  const OPTOUT_KEY = "dorahi:coupon:optout";

  // console.log, not console.debug: DevTools hides debug output on its default
  // "Default levels" filter, which made these diagnostics invisible in practice.
  const debug = (/** @type {string} */ message) =>
    console.log(`[dorahi-cart] ${message}`);

  /** @type {(key: string) => string} */
  const readFlag = (key) => {
    try {
      return sessionStorage.getItem(key) || "";
    } catch {
      // Private mode / storage disabled: fall back to always auto-applying.
      return "";
    }
  };

  /** @type {(key: string, value: string) => void} */
  const writeFlag = (key, value) => {
    try {
      if (value) sessionStorage.setItem(key, value);
      else sessionStorage.removeItem(key);
    } catch {
      /* storage unavailable — auto-apply just stays in charge */
    }
  };

  const manualChoice = () => readFlag(MANUAL_KEY);

  // Passing null hands control back to auto-apply.
  /** @type {(code: string | null) => void} */
  const setManualChoice = (code) => {
    writeFlag(MANUAL_KEY, code ? code.toUpperCase() : "");
    // Choosing a coupon means they're engaging with offers again, so an
    // earlier removal shouldn't keep auto-apply switched off.
    if (code) writeFlag(OPTOUT_KEY, "");
  };

  // Identifies this exact bag. "Remove" is remembered against it so the code
  // can't come straight back, while any change to the contents lets auto-apply
  // resume — a session-wide opt-out would silently kill it for the whole visit.
  /** @type {(cart: any) => string} */
  const cartSignature = (cart) =>
    (cart.items || [])
      .map((/** @type {any} */ i) => `${i.key}x${i.quantity}`)
      .join("|");

  // One read of the real cart: which codes it carries, what they're actually
  // worth, and the bag signature. The `applicable` flag is deliberately
  // ignored everywhere — on this store it reports false for codes that are
  // demonstrably applied and saving money (HOS_BOGO showed "not accepted"
  // while the rendered cart listed it saving ₹799). Money is the only signal
  // that can't lie: original_total_price − total_price covers order-level
  // discounts, total_discount covers line-level ones.
  /** @type {() => Promise<{codes: string[], saving: number, signature: string} | null>} */
  const serverCart = async () => {
    try {
      const cart = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      }).then((r) => r.json());
      const delta =
        (cart.original_total_price || 0) - (cart.total_price || 0);
      return {
        codes: (cart.discount_codes || []).map((/** @type {any} */ d) =>
          (d.code || "").toUpperCase(),
        ),
        saving: Math.max(delta, cart.total_discount || 0),
        signature: cartSignature(cart),
      };
    } catch {
      return null;
    }
  };

  /** @type {(value: string) => Promise<any>} */
  const postCartDiscount = async (value) => {
    const routes = window.Theme?.routes ?? {};
    const response = await fetch(routes.cart_update_url || "/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ discount: value }),
    });
    return response.json();
  };

  // Replaces whatever is on the cart with `code` (empty string just clears).
  //
  // Clearing first is not optional. /cart/update.js does not drop the existing
  // code when a new one arrives, so the incoming code lands *alongside* it —
  // and since every code on this store is combinesWith:false, Shopify then
  // refuses the newcomer for not combining with the one already there. That is
  // what produced "FESTIVE20 is not applicable to your cart" while B1G1 was on,
  // despite FESTIVE20 having no minimum and covering every product.
  /** @type {(code: string) => Promise<any>} */
  const setCartDiscount = async (code) => {
    const cleared = await postCartDiscount("");
    if (!code) return cleared;
    return postCartDiscount(code);
  };

  // Applies `code` and judges success purely by outcome: the code sits on the
  // fresh cart AND the cart is saving money. One rule for every discount class
  // — a FESTIVE20 that landed always saves, a B1G1 with nothing to give away
  // saves ₹0 and correctly reads as not-landed.
  /** @type {(code: string) => Promise<boolean>} */
  const postDiscount = async (code) => {
    await setCartDiscount(code);
    const server = await serverCart();
    if (!server) return false;
    return server.codes.includes(code.toUpperCase()) && server.saving > 0;
  };

  // The whole rule: B1G1 when the bag qualifies, the fallback otherwise. Two
  // things override it — a coupon the customer picked themselves, and a
  // removal — and both are scoped so they can't outlive the bag they applied to.
  const syncAutoCoupon = async () => {
    const root = q("[data-dorahi-cart-swap-root]");
    if (!root) return debug("no cart on this page");
    if (mutating) return debug("skipped: another cart update is running");

    const desired = (root.dataset.autoCoupon || "").toUpperCase();
    const fallback = (root.dataset.fallbackCoupon || "").toUpperCase();

    const server = await serverCart();
    if (!server) return debug("could not read /cart.js");
    const { codes: applied, signature } = server;

    // They removed the coupon from *this* bag, so leave it alone. Change the
    // bag and the offer is back in play.
    if (readFlag(OPTOUT_KEY)) {
      if (readFlag(OPTOUT_KEY) === signature) {
        return debug("skipped: coupon was removed from this cart");
      }
      writeFlag(OPTOUT_KEY, "");
    }

    // A remembered manual pick only counts while it's still on the cart.
    const chosen = manualChoice();
    if (chosen) {
      if (applied.includes(chosen)) return debug(`keeping ${chosen} (chosen)`);
      setManualChoice(null);
    }

    // "Already applied" needs money behind it: a B1G1 code sitting on a bag
    // that no longer qualifies saves nothing and should be swapped out.
    if (desired && applied.includes(desired) && server.saving > 0) {
      return debug(`${desired} already applied (saving ₹${server.saving / 100})`);
    }
    if (!desired && applied.length === 0) return;

    mutating = true;
    try {
      let landed = false;
      if (desired) {
        landed = await postDiscount(desired);
        debug(`${desired} -> ${landed ? "applied" : "not accepted"}`);
      }

      // Shopify is the authority on eligibility, so if it won't take the code
      // we wanted (say the bag no longer really qualifies for B1G1) drop to the
      // fallback rather than leaving the cart with nothing.
      if (!landed && fallback && fallback !== desired) {
        landed = await postDiscount(fallback);
        debug(`${fallback} (fallback) -> ${landed ? "applied" : "not accepted"}`);
      }

      if (!landed) debug("no coupon could be applied to this cart");
    } catch (error) {
      console.warn("[dorahi-cart] auto coupon failed:", error);
      return;
    } finally {
      mutating = false;
    }

    await refresh();
  };

  const updateDiscounts = async (
    codes,
    { successCode = null, silent = false } = {},
  ) => {
    if (mutating && !(await waitForIdle())) return;
    mutating = true;

    // Only restore the coupons sheet after the refresh if the user had it open
    // (apply from inside the sheet). Applying from the hint banner must NOT
    // pop the sheet open.
    const sheetWasOpen = isSheetOpen();

    try {
      // Exactly one code ever lives on the cart, and setCartDiscount clears the
      // previous one first — without that, swapping B1G1 for another code is
      // refused outright, because the two can't combine.
      debug(`swap requested: ${codes[0] || "(clear)"}`);
      await setCartDiscount(codes[0] || "");

      // Success is judged by outcome, never by the response's `applicable`
      // flag — on this store that flag reports false for codes that are
      // saving real money, and trusting it made this handler clear its own
      // successful swaps and show a bogus "not applicable" error.
      const server = await serverCart();
      debug(
        `cart now holds: ${server ? server.codes.join(", ") || "no code" : "?"}` +
          `${server ? ` (saving ₹${server.saving / 100})` : ""}`,
      );

      const rejected =
        successCode &&
        server &&
        (server.codes.includes(successCode.toUpperCase()) === false ||
          server.saving === 0);

      if (rejected) {
        // Leave the cart clean rather than holding a code Shopify won't honour.
        await setCartDiscount("").catch(() => {});
        // A code the customer picked that Shopify won't take shouldn't keep
        // auto-apply switched off.
        setManualChoice(null);
        await refresh({ keepSheetOpen: sheetWasOpen });
        showCouponMessage(
          "error",
          `${successCode} is not applicable to your cart`,
        );
        return;
      }

      // Totals changed; re-render from the server so items, coupon list and
      // the breakdown all match the real cart.
      await refresh({ keepSheetOpen: sheetWasOpen });

      if (successCode && !silent) {
        showCouponMessage("success", `${successCode} applied successfully`);
        showCouponPopup(successCode);
      }
    } catch (error) {
      console.warn("[dorahi-cart] discount update failed:", error);
      if (!silent) {
        showCouponMessage(
          "error",
          "Could not update coupon. Please try again.",
        );
      }
    } finally {
      mutating = false;
    }
  };

  /* ---------------- checkout (Shiprocket buyDirect) ---------------- */

  const checkout = async (button) => {
    const label = button.querySelector("[data-checkout-label]");
    button.disabled = true;
    if (label) label.textContent = "Processing...";

    try {
      const ajaxCart = await fetch("/cart.js", {
        headers: { Accept: "application/json" },
      }).then((r) => r.json());
      const products = (ajaxCart.items || []).map((item) => ({
        variantId: String(item.variant_id),
        quantity: item.quantity,
      }));

      if (window.shiprocketCheckoutEvents?.buyDirect) {
        window.shiprocketCheckoutEvents.buyDirect({
          type: "cart",
          products,
          couponCode: button.dataset.couponCode || "",
        });
      } else {
        window.location.href = "/checkout";
        return;
      }
    } catch (error) {
      console.warn("[dorahi-cart] checkout failed:", error);
      window.location.href = "/checkout";
      return;
    } finally {
      setTimeout(() => {
        button.disabled = false;
        if (label) label.textContent = "Proceed to Checkout";
      }, 1500);
    }
  };

  /* ---------------- delegated events ---------------- */

  document.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Open / close
    if (target.closest("[data-cart-open]")) {
      event.preventDefault();
      openDrawer();
      return;
    }
    if (
      target.closest("[data-cart-close]") ||
      target.closest("[data-cart-backdrop]")
    ) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    // Quantity + / -
    const qty = target.closest("[data-cart-qty]");
    if (qty instanceof HTMLElement) {
      event.preventDefault();
      changeLine(
        qty,
        Number(qty.dataset.line),
        Math.max(0, Number(qty.dataset.targetQty)),
      );
      return;
    }

    // Remove line
    const remove = target.closest("[data-cart-remove]");
    if (remove instanceof HTMLElement) {
      event.preventDefault();
      remove.querySelector("[data-remove-icon]")?.classList.add("hidden");
      remove.querySelector("[data-remove-spinner]")?.classList.remove("hidden");
      changeLine(remove, Number(remove.dataset.line), 0);
      return;
    }

    // Coupon sheet
    if (target.closest("[data-coupons-open]")) {
      openSheet();
      return;
    }
    if (
      target.closest("[data-coupons-close]") ||
      target.closest("[data-coupons-backdrop]")
    ) {
      closeSheet();
      return;
    }

    // Apply / remove coupon
    const apply = target.closest("[data-coupon-apply]");
    if (apply instanceof HTMLElement) {
      event.preventDefault();
      const code = apply.dataset.couponApply || "";
      if (!code) return;
      if (apply instanceof HTMLButtonElement) apply.disabled = true;
      apply.textContent = "Applying...";
      // Codes don't combine, so this replaces whatever is on the cart rather
      // than adding to it — picking FESTIVE20 over B1G1 swaps them out.
      setManualChoice(code);
      updateDiscounts([code], { successCode: code });
      return;
    }

    const removeCoupon = target.closest("[data-coupon-remove]");
    if (removeCoupon instanceof HTMLElement) {
      event.preventDefault();
      removeCoupon.textContent = "Removing...";
      setManualChoice(null);
      // Codes never combine, so there's only ever one to clear. The removal is
      // remembered against this exact bag so nothing jumps back in — but only
      // for this bag, so adding or changing items lets offers resume.
      updateDiscounts([]).then(async () => {
        const server = await serverCart();
        if (server) writeFlag(OPTOUT_KEY, server.signature);
      });
      return;
    }

    if (
      target.closest("[data-coupon-popup-close]") ||
      target.closest("[data-coupon-popup-backdrop]")
    ) {
      hideCouponPopup();
      return;
    }

    // Price breakdown
    if (target.closest("[data-breakdown-toggle]")) {
      const breakdown = q("[data-breakdown]");
      const chevron = q("[data-breakdown-chevron]");
      if (!breakdown) return;
      const open = breakdown.classList.contains("max-h-[400px]");
      breakdown.classList.toggle("max-h-[400px]", !open);
      breakdown.classList.toggle("opacity-100", !open);
      breakdown.classList.toggle("max-h-0", open);
      breakdown.classList.toggle("opacity-0", open);
      chevron?.classList.toggle("rotate-180", !open);
      return;
    }

    const checkoutButton = target.closest("[data-checkout]");
    if (checkoutButton instanceof HTMLElement) {
      event.preventDefault();
      checkout(checkoutButton);
    }
  });

  document.addEventListener("submit", (event) => {
    const form =
      event.target instanceof Element
        ? event.target.closest("[data-coupon-form]")
        : null;
    if (!form) return;
    event.preventDefault();

    const input = form.querySelector('input[name="code"]');
    const code = (input instanceof HTMLInputElement ? input.value : "")
      .trim()
      .toUpperCase();
    if (!code) return;

    form.querySelector("[data-coupon-form-idle]")?.classList.add("hidden");
    form
      .querySelector("[data-coupon-form-loading]")
      ?.classList.remove("hidden");

    // Typed codes replace too, and count as the customer's own choice.
    setManualChoice(code);
    updateDiscounts([code], { successCode: code });
  });

  // Keep the typed value uppercase, like the React input.
  document.addEventListener("input", (event) => {
    const input =
      event.target instanceof Element
        ? event.target.closest('[data-coupon-form] input[name="code"]')
        : null;
    if (input instanceof HTMLInputElement)
      input.value = input.value.toUpperCase();
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    if (isSheetOpen()) {
      closeSheet();
      return;
    }
    if (isOpen()) closeDrawer();
  });

  // add-to-cart.tsx called openCart() after a successful add.
  document.addEventListener("dorahi:cart:added", async () => {
    await refresh();
    await syncAutoCoupon();
    openDrawer();
  });
  document.addEventListener("dorahi:cart:open", openDrawer);

  // A cart carried over from an earlier visit may be holding the wrong code (or
  // none), so settle it once on load. The swap root only exists for a non-empty
  // cart, which keeps this from touching an empty one.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => syncAutoCoupon());
  } else {
    syncAutoCoupon();
  }
}
